/**
 * Styles for FunctionPickerPanel. Structural values that intentionally bypass
 * tokens: 1px borders/separators, 9px micro-typography (return-type pill),
 * fixed icon/button sizes (18px / 24px), 0.3px letter-spacing, 0.4 idle
 * opacity, 320px tooltip max-width, 0.01ms reduced-motion sentinel
 * (see portal/AGENTS.md).
 */

import { makeStyles, shorthands, tokens } from '@coreai/fluentui-react';
import { thinScrollbarStyles } from '@/styles/scrollbar';

export const useFunctionPickerStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
  },
  searchRow: {
  
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    flexShrink: 0,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
  },
  searchInput: {
    width: '100%',
  },
  list: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: tokens.spacingVerticalS,
    ...thinScrollbarStyles,
  },
  group: {
    marginBottom: tokens.spacingVerticalS,
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    width: '100%',
    backgroundColor: 'transparent',
    ...shorthands.border('0'),
    cursor: 'pointer',
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    textAlign: 'left',
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
    userSelect: 'none',
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  groupChevron: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    transitionProperty: 'transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    flexShrink: 0,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  groupChevronExpanded: {
    transform: 'rotate(90deg)',
  },
  groupLabel: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  groupBadge: {
    flexShrink: 0,
  },
  itemList: {
    paddingLeft: '36px',
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },
  item: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    minHeight: '36px',
    padding: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    width: '100%',
    backgroundColor: 'transparent',
    ...shorthands.border('0'),
    color: tokens.colorNeutralForeground1,
    cursor: 'grab',
    textAlign: 'left',
    borderRadius: tokens.borderRadiusSmall,
    transitionProperty: 'background-color, transform, box-shadow',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      boxShadow: `inset 0 0 0 1px ${tokens.colorNeutralStroke2}`,
    },
    // Light up the always-visible insert button on row hover/focus. Targets
    // a data attribute so we don't depend on griffel-generated class names.
    ':hover [data-insert-button]': {
      opacity: 1,
      color: tokens.colorBrandForeground1,
    },
    ':focus-visible [data-insert-button]': {
      opacity: 1,
      color: tokens.colorBrandForeground1,
    },
    ':active': {
      cursor: 'grabbing',
      transform: 'scale(0.98)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':active': {
        transform: 'none',
      },
    },
    '@media (forced-colors: active)': {
      ':hover': {
        outline: '1px solid Highlight',
        outlineOffset: '-1px',
      },
    },
  },
  itemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
      color: 'HighlightText',
    },
  },
  itemDragging: {
    opacity: 0.5,
    backgroundColor: tokens.colorBrandBackground2,
  },
  itemTypeIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  itemInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  itemName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: tokens.lineHeightBase200,
  },
  itemSignature: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontFamily: tokens.fontFamilyMonospace,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: tokens.lineHeightBase100,
  },
  // Always-visible insert button. Idle at low opacity; brand color on row
  // hover/focus via the parent's `[data-insert-button]` selector. The row
  // body is reserved for selection/expand — clicking the row never inserts.
  insertButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Match WorkflowInputPanel.outputCopyButton so the right-edge action
    // sits in the same place on both tabs.
    width: '28px',
    height: '28px',
    flexShrink: 0,
    marginLeft: 'auto',
    padding: 0,
    color: tokens.colorNeutralForeground3,
    backgroundColor: 'transparent',
    ...shorthands.border('0'),
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'pointer',
    fontSize: tokens.fontSizeBase300,
    opacity: 0.4,
    transitionProperty: 'opacity, color, background-color, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
      opacity: 1,
      color: tokens.colorBrandForeground1,
    },
    ':active': {
      transform: 'scale(0.94)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-2px',
      opacity: 1,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':active': {
        transform: 'none',
      },
    },
    '@media (forced-colors: active)': {
      forcedColorAdjust: 'none',
      ':hover': {
        outline: '1px solid Highlight',
        outlineOffset: '-1px',
      },
    },
  },
  emptyState: {
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  tooltipBody: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    maxWidth: '320px',
    // Tooltip is purely informational. Disable pointer events so clicks pass
    // through to the row underneath if dismissal lags a fast cursor.
    pointerEvents: 'none',
  },
  tooltipHeader: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  tooltipName: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  tooltipSignature: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  tooltipDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase200,
  },
});
